// packages/shared/api-client/endpoints/grievances.js
// Grievance system API endpoints

import { baseApi } from "../baseApi";

/**
 * Grievance API endpoints
 * Provides grievance submission, viewing, and management
 */
const grievanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all grievances (filtered by permissions)
    getGrievances: builder.query({
      query: (params = {}) => ({
        url: '/api/grievances',
        params, // { page, limit, status, department, type }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Grievance', id })),
              { type: 'Grievance', id: 'LIST' },
            ]
          : [{ type: 'Grievance', id: 'LIST' }],
    }),

    // Get specific grievance by ID
    getGrievanceById: builder.query({
      query: (id) => `/api/grievances/${id}`,
      providesTags: (result, error, id) => [{ type: 'Grievance', id }],
    }),

    // Submit new grievance
    createGrievance: builder.mutation({
      query: (grievanceData) => ({
        url: '/api/grievances',
        method: 'POST',
        body: grievanceData,
      }),
      invalidatesTags: [{ type: 'Grievance', id: 'LIST' }],
    }),

    // Update grievance (status, assignment, etc.)
    updateGrievance: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/grievances/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Grievance', id },
        { type: 'Grievance', id: 'LIST' },
      ],
    }),

    // Add comment to grievance
    addGrievanceComment: builder.mutation({
      query: ({ id, content, isInternal }) => ({
        url: `/api/grievances/${id}/comments`,
        method: 'POST',
        body: { content, isInternal },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Grievance', id }],
    }),

    // Upload document to grievance (returns presigned URL)
    uploadGrievanceDocument: builder.mutation({
      query: ({ id, fileName, fileType, fileSize }) => ({
        url: `/api/grievances/${id}/documents`,
        method: 'POST',
        body: { fileName, fileType, fileSize },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Grievance', id }],
    }),

    // Download document (get presigned URL)
    downloadGrievanceDocument: builder.mutation({
      query: ({ grievanceId, documentId }) => ({
        url: `/api/grievances/${grievanceId}/documents/${documentId}/download`,
        method: 'GET',
      }),
    }),

    // Delete document
    deleteGrievanceDocument: builder.mutation({
      query: ({ grievanceId, documentId }) => ({
        url: `/api/grievances/${grievanceId}/documents/${documentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
      ],
    }),

    // Get grievance analytics (committee only)
    getGrievanceAnalytics: builder.query({
      query: (params = {}) => ({
        url: '/api/grievances/analytics',
        params, // { startDate, endDate, department }
      }),
    }),

    // ========================================================================
    // WORKFLOW MANAGEMENT ENDPOINTS
    // ========================================================================

    // Get all workflow templates
    getWorkflowTemplates: builder.query({
      query: (params = {}) => ({
        url: '/api/grievances/workflows',
        params, // { departmentId }
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'GrievanceWorkflow', id })),
              { type: 'GrievanceWorkflow', id: 'LIST' },
            ]
          : [{ type: 'GrievanceWorkflow', id: 'LIST' }],
    }),

    // Get workflow template by ID
    getWorkflowTemplateById: builder.query({
      query: (id) => `/api/grievances/workflows/${id}`,
      providesTags: (result, error, id) => [{ type: 'GrievanceWorkflow', id }],
    }),

    // Create workflow template
    createWorkflowTemplate: builder.mutation({
      query: (workflowData) => ({
        url: '/api/grievances/workflows',
        method: 'POST',
        body: workflowData,
      }),
      invalidatesTags: [{ type: 'GrievanceWorkflow', id: 'LIST' }],
    }),

    // Update workflow template
    updateWorkflowTemplate: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/grievances/workflows/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'GrievanceWorkflow', id },
        { type: 'GrievanceWorkflow', id: 'LIST' },
      ],
    }),

    // Delete workflow template
    deleteWorkflowTemplate: builder.mutation({
      query: (id) => ({
        url: `/api/grievances/workflows/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'GrievanceWorkflow', id: 'LIST' }],
    }),

    // Activate workflow
    activateWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/grievances/workflows/${id}/activate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GrievanceWorkflow', id },
        { type: 'GrievanceWorkflow', id: 'LIST' },
      ],
    }),

    // Deactivate workflow
    deactivateWorkflow: builder.mutation({
      query: (id) => ({
        url: `/api/grievances/workflows/${id}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'GrievanceWorkflow', id },
        { type: 'GrievanceWorkflow', id: 'LIST' },
      ],
    }),

    // Clone workflow to another department
    cloneWorkflow: builder.mutation({
      query: ({ id, targetDepartmentId }) => ({
        url: `/api/grievances/workflows/${id}/clone`,
        method: 'POST',
        body: { targetDepartmentId },
      }),
      invalidatesTags: [{ type: 'GrievanceWorkflow', id: 'LIST' }],
    }),

    // ========================================================================
    // WORKFLOW PROGRESS ENDPOINTS
    // ========================================================================

    // Get workflow progress for a grievance
    getGrievanceWorkflow: builder.query({
      query: (grievanceId) => `/api/grievances/${grievanceId}/workflow`,
      providesTags: (result, error, grievanceId) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Advance grievance to next step
    advanceGrievanceStep: builder.mutation({
      query: ({ grievanceId, actionTaken, notes }) => ({
        url: `/api/grievances/${grievanceId}/workflow/advance`,
        method: 'POST',
        body: { actionTaken, notes },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'Grievance', id: 'LIST' },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Complete current step (without advancing)
    completeCurrentStep: builder.mutation({
      query: ({ grievanceId, actionTaken, notes }) => ({
        url: `/api/grievances/${grievanceId}/workflow/complete-step`,
        method: 'POST',
        body: { actionTaken, notes },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Schedule meeting for current step
    scheduleStepMeeting: builder.mutation({
      query: ({ grievanceId, meetingDate }) => ({
        url: `/api/grievances/${grievanceId}/workflow/schedule-meeting`,
        method: 'POST',
        body: { meetingDate },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Record that meeting was held
    recordStepMeeting: builder.mutation({
      query: ({ grievanceId, meetingNotes }) => ({
        url: `/api/grievances/${grievanceId}/workflow/record-meeting`,
        method: 'POST',
        body: { meetingNotes },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Extend deadline for current step
    extendStepDeadline: builder.mutation({
      query: ({ grievanceId, additionalDays, reason }) => ({
        url: `/api/grievances/${grievanceId}/workflow/extend-deadline`,
        method: 'POST',
        body: { additionalDays, reason },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),

    // Withdraw grievance
    withdrawGrievance: builder.mutation({
      query: ({ grievanceId, reason }) => ({
        url: `/api/grievances/${grievanceId}/workflow/withdraw`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (result, error, { grievanceId }) => [
        { type: 'Grievance', id: grievanceId },
        { type: 'Grievance', id: 'LIST' },
        { type: 'GrievanceWorkflow', id: 'PROGRESS' },
      ],
    }),
  }),
});

// Export hooks for usage in components
export const useGetGrievancesQuery = grievanceApi.useGetGrievancesQuery;
export const useLazyGetGrievancesQuery = grievanceApi.useLazyGetGrievancesQuery;
export const useGetGrievanceByIdQuery = grievanceApi.useGetGrievanceByIdQuery;
export const useCreateGrievanceMutation = grievanceApi.useCreateGrievanceMutation;
export const useUpdateGrievanceMutation = grievanceApi.useUpdateGrievanceMutation;
export const useAddGrievanceCommentMutation = grievanceApi.useAddGrievanceCommentMutation;
export const useUploadGrievanceDocumentMutation = grievanceApi.useUploadGrievanceDocumentMutation;
export const useDownloadGrievanceDocumentMutation = grievanceApi.useDownloadGrievanceDocumentMutation;
export const useDeleteGrievanceDocumentMutation = grievanceApi.useDeleteGrievanceDocumentMutation;
export const useGetGrievanceAnalyticsQuery = grievanceApi.useGetGrievanceAnalyticsQuery;

// Workflow management hooks
export const useGetWorkflowTemplatesQuery = grievanceApi.useGetWorkflowTemplatesQuery;
export const useGetWorkflowTemplateByIdQuery = grievanceApi.useGetWorkflowTemplateByIdQuery;
export const useCreateWorkflowTemplateMutation = grievanceApi.useCreateWorkflowTemplateMutation;
export const useUpdateWorkflowTemplateMutation = grievanceApi.useUpdateWorkflowTemplateMutation;
export const useDeleteWorkflowTemplateMutation = grievanceApi.useDeleteWorkflowTemplateMutation;
export const useActivateWorkflowMutation = grievanceApi.useActivateWorkflowMutation;
export const useDeactivateWorkflowMutation = grievanceApi.useDeactivateWorkflowMutation;
export const useCloneWorkflowMutation = grievanceApi.useCloneWorkflowMutation;

// Workflow progress hooks
export const useGetGrievanceWorkflowQuery = grievanceApi.useGetGrievanceWorkflowQuery;
export const useAdvanceGrievanceStepMutation = grievanceApi.useAdvanceGrievanceStepMutation;
export const useCompleteCurrentStepMutation = grievanceApi.useCompleteCurrentStepMutation;
export const useScheduleStepMeetingMutation = grievanceApi.useScheduleStepMeetingMutation;
export const useRecordStepMeetingMutation = grievanceApi.useRecordStepMeetingMutation;
export const useExtendStepDeadlineMutation = grievanceApi.useExtendStepDeadlineMutation;
export const useWithdrawGrievanceMutation = grievanceApi.useWithdrawGrievanceMutation;
